/**
 * useOccupancy — reads pre-computed hourly occupancy averages for a gym.
 * Data lives at `gyms/{gymId}/public/occupancy` (written by the updateAllGymOccupancy routine of the dailyMaintenance dispatcher).
 * One-time fetch — averages only change daily.
 */
import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * The occupancy doc's data, or `null` when the gym has none. A read error is
 * thrown: `useOccupancy` treats it as "no data", the admin statistics source
 * (`useAdminStatsSources`, docs/spec/01f-tab-loading.md) logs it.
 *
 * @param {string} gymId
 * @returns {Promise<Object|null>}
 */
export async function loadOccupancy(gymId) {
  const snap = await getDoc(doc(db, "gyms", gymId, "public", "occupancy"));
  return snap.exists() ? snap.data() : null;
}

/**
 * @param {string|null} gymId
 * @returns {{ occupancy: Object|null, loading: boolean }}
 */
export function useOccupancy(gymId) {
  const [occupancy, setOccupancy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gymId) { setLoading(false); return; }
    let cancelled = false;
    loadOccupancy(gymId)
      .then(data => {
        if (!cancelled && data) {
          setOccupancy(data);
        }
        if (!cancelled) setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [gymId]);

  return { occupancy, loading };
}
